/**
 * Normalises the two legacy-portal digital-passport responses.
 *
 *   POST /digital_passport/query  -> the vehicle's other inspections
 *   POST /digital_passport/report -> one of them, paired with the current case
 *
 * The question the feature answers is whether damage on this claim was already
 * there. So the comparison is not two lists side by side: it is a per-part
 * verdict — pre-existing, new, resolved or clear — because "was the grille
 * already broken?" is the thing a reviewer is actually deciding.
 */

const asArray = (v) => (Array.isArray(v) ? v : []);

/** Both endpoints nest a JSON document inside a string field. */
const parseBlob = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// "pi" is a pre-inspection (cover being written); "claim" is a loss.
const INSPECTION_LABELS = { pi: "Pre-inspection", claim: "Claim" };
export const inspectionLabel = (type) =>
  INSPECTION_LABELS[type] || (type ? String(type) : "Inspection");

/** intensity arrives as a string ("0", "10"); anything above zero is damage. */
const intensityOf = (part) => Number(part?.intensity) || 0;
const isDamaged = (part) => intensityOf(part) > 0;

/** "broke, broken, scratch" -> ["broke", "broken", "scratch"]; "-" -> [] */
export const damageTypes = (value) =>
  typeof value === "string" && value.trim() && value.trim() !== "-"
    ? value
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

export const toHistory = (response, { currentRefNum } = {}) => {
  if (!response) return null;

  const cases = asArray(response.cases).map((entry) => ({
    refNum: entry.ref_num,
    sourceName: entry.source_name,
    inspectionType: entry.inspection_type,
    inspectionLabel: inspectionLabel(entry.inspection_type),
    assessedAt: entry.assessed_at || null,
    createdAt: entry.created_at || null,
    coverPic: entry.cover_pic || null,
    numPlate: entry.num_plate_norm || null,
    vin: entry.vin_norm || null,
    // The search matches by vehicle, so the current case comes back in its own
    // history. Flagged rather than filtered: seeing it confirms the match worked.
    isCurrent: Boolean(currentRefNum) && entry.ref_num === currentRefNum,
  }));

  // Newest first — the API returns them unordered.
  const sorted = [...cases].sort((a, b) => {
    const at = Date.parse(a.assessedAt || a.createdAt || 0) || 0;
    const bt = Date.parse(b.assessedAt || b.createdAt || 0) || 0;
    return bt - at;
  });

  return {
    ok: response.ok !== false,
    searchParam: response.search_param ?? null,
    cases: sorted,
    priorCount: sorted.filter((c) => !c.isCurrent).length,
  };
};

/**
 * Per-part verdicts between a prior inspection and the current one.
 *
 * Joined on AssemblyName, which is what both payloads key parts by. A part
 * present in only one of the two still appears — absence is not the same as
 * undamaged, and silently dropping it would hide exactly the case a reviewer
 * cares about.
 */
/**
 * Verdict labels depend on which inspection came first.
 *
 * The history is not necessarily older than the case being viewed — a vehicle
 * inspected again after this claim comes back in the same search. Calling that
 * later inspection's damage "pre-existing", or this case's "new", would state the
 * opposite of the truth. So the insurance framing is used only when the compared
 * inspection genuinely predates this one; otherwise the labels are directionless
 * and the dates carry the meaning.
 */
export const VERDICTS = {
  pre_existing: { label: "Pre-existing", tone: "warn" },
  new: { label: "New damage", tone: "bad" },
  resolved: { label: "No longer reported", tone: "good" },
  clear: { label: "Clear in both", tone: "muted" },
};

const VERDICTS_UNORDERED = {
  pre_existing: { label: "In both", tone: "warn" },
  new: { label: "Only on this case", tone: "bad" },
  resolved: { label: "Only on compared", tone: "good" },
  clear: { label: "Clear in both", tone: "muted" },
};

export const verdictLabels = (comparedIsEarlier) =>
  comparedIsEarlier ? VERDICTS : VERDICTS_UNORDERED;

const verdictFor = (prior, current) => {
  const priorDamaged = prior ? isDamaged(prior) : false;
  const currentDamaged = current ? isDamaged(current) : false;
  if (priorDamaged && currentDamaged) return "pre_existing";
  if (!priorDamaged && currentDamaged) return "new";
  if (priorDamaged && !currentDamaged) return "resolved";
  return "clear";
};


// Sides in the order a reviewer walks around the car, so the picker does not
// reorder itself between cases. Anything unrecognised keeps its own order after.
const SIDE_ORDER = [
  "front",
  "front_left",
  "front_right",
  "left",
  "right",
  "rear",
  "rear_left",
  "rear_right",
];

const sideRank = (side) => {
  const i = SIDE_ORDER.indexOf(side);
  return i === -1 ? SIDE_ORDER.length : i;
};

export const sideLabel = (side) =>
  typeof side === "string" ? side.replace(/[-_]+/g, " ").toUpperCase() : "";

/**
 * The frame to show for one part on one side.
 *
 * A part is photographed from several angles and, once damaged, also gets a
 * cropped damage frame under /partdamages/. Prefer that one: it is the annotated
 * evidence. The plain /parts/ crop is what an undamaged inspection has, which is
 * exactly what the earlier side of the comparison needs.
 */
const pictureForSide = (pictures, side) => {
  const onSide = asArray(pictures).filter((p) => p?.side === side && p?.path);
  if (!onSide.length) return null;
  const annotated = onSide.find((p) => p.path.includes("/partdamages/"));
  return (annotated || onSide[0]).path;
};

const fullCarBySide = (blob) => {
  const map = new Map();
  asArray(blob?.full_car_images).forEach((img) => {
    if (img?.side && img?.picture && !map.has(img.side)) {
      map.set(img.side, img.picture);
    }
  });
  return map;
};

/**
 * Per-side comparison: the whole-car view from each inspection, plus every part
 * photographed from that side, with its frame from each.
 *
 * Clear-in-both parts are included but counted separately, so a view can lead
 * with what differs and still show a panel a reviewer asks to see.
 */
const buildSideViews = (priorBlob, currentBlob, parts) => {
  const priorCar = fullCarBySide(priorBlob);
  const currentCar = fullCarBySide(currentBlob);

  const sides = new Set([...priorCar.keys(), ...currentCar.keys()]);
  parts.forEach((part) => {
    [...part.priorPictures, ...part.currentPictures].forEach((pic) => {
      if (pic?.side) sides.add(pic.side);
    });
  });

  return [...sides]
    .sort((a, b) => sideRank(a) - sideRank(b) || a.localeCompare(b))
    .map((side) => {
      let clearOnSide = 0;
      const sideParts = [];

      parts.forEach((part) => {
        const prior = pictureForSide(part.priorPictures, side);
        const current = pictureForSide(part.currentPictures, side);
        if (!prior && !current) return;
        if (part.verdict === "clear") clearOnSide += 1;
        sideParts.push({
          name: part.name,
          verdict: part.verdict,
          priorIntensity: part.priorIntensity,
          currentIntensity: part.currentIntensity,
          prior,
          current,
        });
      });

      return {
        side,
        label: sideLabel(side),
        prior: priorCar.get(side) || null,
        current: currentCar.get(side) || null,
        parts: sideParts,
        changedCount: sideParts.filter((p) => p.verdict !== "clear").length,
        clearCount: clearOnSide,
      };
    });
};

export const toComparison = (response) => {
  const data = response?.data;
  if (!data) return null;

  const priorBlob = parseBlob(data.assessment_json);
  const currentBlob = parseBlob(data.current_assessment_json);

  const index = (blob) => {
    const map = new Map();
    asArray(blob?.Assessment).forEach((part) => {
      if (part?.AssemblyName) map.set(part.AssemblyName, part);
    });
    return map;
  };

  const priorParts = index(priorBlob);
  const currentParts = index(currentBlob);

  const names = [...new Set([...priorParts.keys(), ...currentParts.keys()])];

  const parts = names
    .map((name) => {
      const prior = priorParts.get(name);
      const current = currentParts.get(name);
      const verdict = verdictFor(prior, current);
      return {
        name,
        verdict,
        priorIntensity: prior ? intensityOf(prior) : null,
        currentIntensity: current ? intensityOf(current) : null,
        priorTypes: damageTypes(prior?.dam_type),
        currentTypes: damageTypes(current?.dam_type),
        priorPictures: asArray(prior?.pictures),
        currentPictures: asArray(current?.pictures),
        inPriorOnly: Boolean(prior) && !current,
        inCurrentOnly: Boolean(current) && !prior,
      };
    })
    // Whatever needs a decision first: new damage, then pre-existing, then the
    // rest. Clear-in-both parts are the bulk and the least interesting.
    .sort((a, b) => {
      const rank = { new: 0, pre_existing: 1, resolved: 2, clear: 3 };
      const byVerdict = rank[a.verdict] - rank[b.verdict];
      if (byVerdict !== 0) return byVerdict;
      return (b.currentIntensity ?? 0) - (a.currentIntensity ?? 0);
    });

  const counts = parts.reduce(
    (acc, part) => {
      acc[part.verdict] = (acc[part.verdict] || 0) + 1;
      return acc;
    },
    { pre_existing: 0, new: 0, resolved: 0, clear: 0 },
  );

  return {
    prior: {
      refNum: data.ref_num,
      sourceName: data.source_name,
      inspectionType: data.inspection_type,
      inspectionLabel: inspectionLabel(data.inspection_type),
      assessedAt: data.assessed_at || null,
      claimNo: priorBlob?.ClaimNo || null,
      versionNo: priorBlob?.VersionNo || null,
      images: asArray(priorBlob?.full_car_images),
    },
    current: {
      refNum: data.current_ref_num,
      sourceName: data.current_source_name,
      inspectionType: data.current_inspection_type,
      inspectionLabel: inspectionLabel(data.current_inspection_type),
      assessedAt: data.current_assessed_at || null,
      claimNo: currentBlob?.ClaimNo || null,
      versionNo: currentBlob?.VersionNo || null,
      images: asArray(currentBlob?.full_car_images),
    },
    parts,
    counts,

    // Which inspection is earlier decides how the verdicts may be phrased.
    // Null when either date is missing — then no ordering claim is made.
    comparedIsEarlier: (() => {
      const a = Date.parse(data.assessed_at);
      const b = Date.parse(data.current_assessed_at);
      if (Number.isNaN(a) || Number.isNaN(b)) return null;
      return a < b;
    })(),

    // One entry per side of the car, each holding the two inspections' view of
    // that side plus the parts photographed from it. Built here rather than in
    // the view because the join is the awkward part: full_car_images is keyed by
    // side, part pictures carry their own side, and a side may exist in only one
    // of the two inspections.
    sideViews: buildSideViews(priorBlob, currentBlob, parts),
    // The headline number: damage on this claim that the earlier inspection
    // already showed.
    preExistingCount: counts.pre_existing,
    newCount: counts.new,
  };
};

/**
 * What, if anything, the vehicle's history is worth interrupting the reviewer
 * about — the input to the banner on the case page.
 *
 * Returns facts and a `kind`, not sentences: the wording belongs with the view,
 * but which of these four situations you are in is a claim about the data and
 * is worth testing.
 *
 *   pre_existing     damage on this claim was already there at an earlier
 *                    inspection. The finding the feature exists to surface.
 *   cleared          an earlier inspection exists and none of this claim's
 *                    damage appears in it. Also a finding, and a good one.
 *   earlier_unknown  earlier inspections exist, not compared yet.
 *   none_earlier     the vehicle has history, but all of it postdates this
 *                    claim — so nothing here can establish pre-existing damage.
 *                    Saying so is the point; the alternative is a reviewer
 *                    reading a later inspection as if it were an earlier one.
 *
 * Null when there is no other inspection at all. Silence is the right answer
 * there; a banner announcing zero records is noise.
 */
export const toPassportAlert = (history, { comparison } = {}) => {
  if (!history) return null;

  const current = history.cases.find((c) => c.isCurrent) || null;
  const others = history.cases.filter((c) => !c.isCurrent);
  if (!others.length) return null;

  const timeOf = (entry) => Date.parse(entry?.assessedAt || entry?.createdAt);
  const currentTime = timeOf(current);

  // Undated entries are not assumed to be earlier — that assumption is exactly
  // the one that produced backwards verdicts before.
  const earlier = Number.isFinite(currentTime)
    ? others
        .filter((entry) => {
          const t = timeOf(entry);
          return Number.isFinite(t) && t < currentTime;
        })
        .sort((a, b) => timeOf(b) - timeOf(a))
    : [];

  const base = {
    total: others.length,
    earlierCount: earlier.length,
    nearestEarlier: earlier[0] || null,
    currentAssessedAt: current?.assessedAt || null,
  };

  if (!earlier.length) return { ...base, kind: "none_earlier", panels: [] };

  if (!comparison || !comparison.comparedIsEarlier) {
    return { ...base, kind: "earlier_unknown", panels: [] };
  }

  const preExisting = comparison.parts.filter((p) => p.verdict === "pre_existing");
  const damagedNow = comparison.parts.filter(
    (p) => p.verdict === "pre_existing" || p.verdict === "new",
  );

  return {
    ...base,
    kind: preExisting.length ? "pre_existing" : "cleared",
    comparedAt: comparison.prior.assessedAt || null,
    comparedRefNum: comparison.prior.refNum || null,
    damagedCount: damagedNow.length,
    alreadyDamagedCount: preExisting.length,
    panels: preExisting.map((p) => p.name),
  };
};

export default toHistory;
